import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CandidateCard } from "@/components/CandidateCard";
import { OntarioMapView, loadWards } from "@/components/OntarioMapView";
import { api } from "@/lib/api";
import { Landmark, MapPin, Users, ShieldCheck, ExternalLink, X } from "lucide-react";

export default function ElectionsMap() {
  const [registry, setRegistry] = useState([]);
  const [query, setQuery] = useState("");
  const [selectedMuni, setSelectedMuni] = useState(null); // registry/feature props
  const [selectedWard, setSelectedWard] = useState(null);
  const [wardsAvailable, setWardsAvailable] = useState([]);
  const [wyvMunis, setWyvMunis] = useState([]);
  const [allCandidates, setAllCandidates] = useState([]);
  const [imported, setImported] = useState([]);

  useEffect(() => {
    api.get("/public/geo/municipalities/registry").then((r) => setRegistry(r.data)).catch(() => {});
    api.get("/public/geo/wards-available").then((r) => setWardsAvailable(r.data)).catch(() => {});
    api.get("/public/municipalities").then((r) => setWyvMunis(r.data)).catch(() => {});
    api.get("/public/candidates").then((r) => setAllCandidates(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setSelectedWard(null);
    setImported([]);
    if (!selectedMuni) return;
    api.get("/public/ontario-candidates", { params: { municipality: selectedMuni.slug } })
      .then((r) => setImported(r.data)).catch(() => {});
    loadWards(selectedMuni.slug); // pre-warm ward layer
  }, [selectedMuni]);

  const matches = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.trim().toLowerCase();
    return registry.filter((m) => m.name.toLowerCase().includes(q)).slice(0, 6);
  }, [query, registry]);

  const wyvMuni = selectedMuni
    ? wyvMunis.find((m) => m.slug === selectedMuni.slug || m.name.toLowerCase().includes(selectedMuni.name.toLowerCase()))
    : null;

  const muniCandidates = wyvMuni
    ? allCandidates.filter((c) => c.municipality?.id === wyvMuni.id)
    : [];
  const wardCandidates = selectedWard
    ? muniCandidates.filter((c) => !c.office?.ward_label || c.office.ward_label === selectedWard.ward_label)
    : muniCandidates;
  const importedShown = selectedWard
    ? imported.filter((c) => !c.ward || c.ward === selectedWard.ward_label)
    : imported;

  return (
    <div className="wyv-container py-12 sm:py-16">
      <div className="max-w-2xl">
        <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200 mb-4">Ontario Election Map</Badge>
        <h1 className="font-heading font-bold text-3xl sm:text-4xl text-foreground" data-testid="elections-map-title">
          Explore Ontario, municipality by municipality.
        </h1>
        <p className="text-muted-foreground mt-3 leading-relaxed">
          Click a municipality to see its election summary. Where ward boundary data exists, zoom in to see
          ward outlines, pick a ward, see who is running — and continue to Witness Your Vote.
        </p>
      </div>

      <div className="mt-6 max-w-md relative">
        <label htmlFor="map-muni-search" className="sr-only">Search municipality</label>
        <Input
          id="map-muni-search"
          placeholder="Search a municipality (e.g. Kawartha Lakes)…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="map-muni-search"
        />
        {matches.length > 0 && (
          <div className="absolute z-20 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg overflow-hidden" data-testid="map-muni-results">
            {matches.map((m) => (
              <button
                key={m.slug}
                type="button"
                onClick={() => { setSelectedMuni(m); setQuery(""); }}
                className="w-full text-left px-4 py-2.5 text-sm hover:bg-secondary flex items-center gap-2"
                data-testid={`map-muni-option-${m.slug}`}
              >
                <Landmark className="h-3.5 w-3.5 text-accent" /> {m.name}
                <span className="text-[10px] text-muted-foreground ml-auto">{m.type}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="mt-6 grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3">
          <OntarioMapView
            focusMuniSlug={selectedMuni?.slug || null}
            focusWardLabel={selectedWard?.ward_label || null}
            onSelectMunicipality={(props) => setSelectedMuni(props)}
            onSelectWard={(props) => setSelectedWard(props)}
            heightClass="h-[420px] sm:h-[560px]"
          />
          <p className="text-[11px] text-muted-foreground mt-2">
            Municipal boundaries: Land Information Ontario (Open Government Licence – Ontario). Ward boundaries shown
            where open data is available. Map © OpenStreetMap contributors. Always confirm details with your municipal Clerk's office.
          </p>
        </div>

        <div className="lg:col-span-2 space-y-4">
          {!selectedMuni && (
            <Card data-testid="map-empty-state">
              <CardContent className="p-6">
                <h2 className="font-heading font-semibold text-base text-foreground mb-2">How to use this map</h2>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex gap-2"><Landmark className="h-4 w-4 text-accent shrink-0 mt-0.5" /> Click any municipality outline (or search above) for its election summary.</li>
                  <li className="flex gap-2"><MapPin className="h-4 w-4 text-accent shrink-0 mt-0.5" /> Ward outlines appear for municipalities with open ward-boundary data.</li>
                  <li className="flex gap-2"><Users className="h-4 w-4 text-accent shrink-0 mt-0.5" /> Pick a ward to see who is running there.</li>
                  <li className="flex gap-2"><ShieldCheck className="h-4 w-4 text-accent shrink-0 mt-0.5" /> Choose a participating candidate to Witness Your Vote.</li>
                </ul>
                <p className="text-xs text-muted-foreground mt-4">
                  Ward data available for: {wardsAvailable.length ? wardsAvailable.join(", ") : "loading…"} (more municipalities as open data becomes available).
                </p>
              </CardContent>
            </Card>
          )}

          {selectedMuni && (
            <Card data-testid="map-muni-panel">
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-accent mb-1">Municipality</p>
                    <h2 className="font-heading font-semibold text-lg text-foreground">{selectedMuni.name}</h2>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {selectedMuni.type}{selectedMuni.upper_tier ? ` · ${selectedMuni.upper_tier}` : ""}
                    </p>
                  </div>
                  <Button variant="ghost" size="icon" aria-label="Clear selection" onClick={() => setSelectedMuni(null)} data-testid="map-clear-muni">
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                {selectedWard && (
                  <div className="mt-3 flex items-center gap-2">
                    <Badge className="bg-amber-100 text-amber-900 border border-amber-300 hover:bg-amber-100" data-testid="map-selected-ward">
                      <MapPin className="h-3 w-3 mr-1" /> {selectedWard.ward_label}
                    </Badge>
                    <button type="button" onClick={() => setSelectedWard(null)} className="text-xs text-muted-foreground hover:text-foreground underline">
                      clear ward
                    </button>
                  </div>
                )}

                {wardsAvailable.includes(selectedMuni.slug) && !selectedWard && (
                  <p className="text-xs text-muted-foreground mt-3">Ward outlines are shown on the map — click one to filter candidates.</p>
                )}
                {!wardsAvailable.includes(selectedMuni.slug) && (
                  <p className="text-xs text-muted-foreground mt-3">Ward boundary open data is not yet available for this municipality.</p>
                )}

                <div className="flex flex-wrap gap-2 mt-4">
                  <Button asChild size="sm" variant="outline"><Link to="/elections">Election Centre</Link></Button>
                  <Button asChild size="sm" variant="outline"><Link to={`/elections/find-my-ward`}>Find My Ward by Address</Link></Button>
                </div>
              </CardContent>
            </Card>
          )}

          {selectedMuni && wyvMuni && wardCandidates.length > 0 && (
            <div data-testid="map-wyv-candidates">
              <h3 className="font-heading font-semibold text-base text-foreground mb-3 inline-flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-accent" /> Participating candidates{selectedWard ? ` — ${selectedWard.ward_label}` : ""}
              </h3>
              <div className="space-y-3">
                {wardCandidates.map((c) => <CandidateCard key={c.id} candidate={c} declareMode />)}
              </div>
            </div>
          )}

          {selectedMuni && wyvMuni && wardCandidates.length === 0 && (
            <Card>
              <CardContent className="p-5 text-sm text-muted-foreground">
                No participating candidate here yet.{" "}
                <Link to="/invite-a-candidate" className="text-primary font-medium hover:underline">Invite one</Link>.
              </CardContent>
            </Card>
          )}

          {selectedMuni && !wyvMuni && (
            <Card>
              <CardContent className="p-5 text-sm text-muted-foreground">
                {selectedMuni.name} is not yet an active Witness Your Vote municipality.{" "}
                <Link to="/contact" className="text-primary font-medium hover:underline">Request it</Link>.
              </CardContent>
            </Card>
          )}

          {selectedMuni && importedShown.length > 0 && (
            <Card data-testid="map-imported-candidates">
              <CardContent className="p-5">
                <h3 className="font-heading font-semibold text-sm text-foreground mb-2 inline-flex items-center gap-2">
                  <Users className="h-4 w-4 text-primary" /> Candidates from official sources
                </h3>
                <ul className="space-y-2">
                  {importedShown.map((c) => (
                    <li key={c.id} className="text-sm text-foreground flex items-start justify-between gap-2">
                      <span>{c.full_name}{c.office ? ` — ${c.office}` : ""}{c.ward ? ` (${c.ward})` : ""}</span>
                      <a href={c.source_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1 shrink-0">
                        Source <ExternalLink className="h-3 w-3" />
                      </a>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
